/* =========================================================================
 * CORRECT THIS FIELD — the control, beside the value it corrects.
 *
 * Brian: "there needs to be a manual push that any of my team can do." The
 * board has been able to FIND a bad record for months; the fix lived in a
 * terminal. This is the fix, on the record, from a phone.
 *
 * ⚠️ IT SITS IN THE RECORD GRID, NOT THE ACTION CONSOLE. The console is the
 * list of things to do NEXT with a record — art, quote, campaign, crown. This
 * is not a next step, it is "this value is wrong", and the answer to that
 * question has to be next to the question. Same placement argument as
 * ClearCrown under the Summary's source line.
 *
 * ⚠️ FOUR PROPERTIES, EACH FROM SOMETHING THAT HAS ALREADY GONE WRONG HERE:
 *
 *   1. THE SERVER DECIDES, THE CLIENT DRAWS. Every field drawn, and whether it
 *      is editable, comes from `GET /api/champion-fields` — never a hardcoded
 *      list. `champion-update` re-decides it against the verified JWT. Nothing
 *      here secures anything (hard rule 5).
 *   2. THE CURRENT VALUE AND THE NEW ONE ARE BOTH ON SCREEN. A blind edit box
 *      on a privileged field is how `sr_state: MA` lands on a California
 *      record — which is the live state of 57419861308 today.
 *   3. A REASON IS MANDATORY AND IT GOES TO THE LEDGER. An edit nobody can
 *      explain later is how this object ended up with a champion's name inside
 *      its own championship title.
 *   4. A REFUSAL NEVER LOOKS LIKE A SUCCESS. `readEditResult` is the only thing
 *      that reads the response, and the record is read back afterwards and
 *      compared. Only a confirmed write, whose field the server NAMED, and
 *      which the re-read agrees with, is drawn as done.
 * ========================================================================= */

import { useState } from 'react'
import {
  FIELD_SPECS, REASON_MIN, mayEditField, editProblem, renderValue,
  currentValue, editPatch, canEditFields, isDerivedField,
} from './fieldEdit.js'
import { runFieldEdit } from './fieldEditRun.js'
import { useAccess } from '../../../lib/access.jsx'
import { ACTIONS } from '../../../lib/rbac.js'
import styles from './pursuit.module.css'

export { canEditFields }

/**
 * The inline control for ONE field. Renders nothing at all when the field is
 * not drawable — a `never` field (`sr_champion_status`, `sr_championship_key`)
 * gets no control, not a disabled one, because there is no question being
 * answered and a greyed-out input invites somebody to ask for the permission.
 */
export default function FieldCorrect({ record, field, policy, onEdited }) {
  const { can, why } = useAccess()
  const [open, setOpen] = useState(false)
  const [next, setNext] = useState(null)
  const [reason, setReason] = useState('')
  const [stage, setStage] = useState('edit') // edit | confirm | working | done
  const [result, setResult] = useState(null)
  const [declared, setDeclared] = useState(false)

  const spec = FIELD_SPECS[field]
  if (!spec || !canEditFields(record) || !policy) return null

  const gate = mayEditField(policy, field)
  if (!gate.drawable) return null

  /* ⚠️ THE CLIENT ACTION IS `CC_CHAMPION_UPDATE` — "Correct championship
   * fields" — which is exec (any record) and rep (own book). It mirrors the
   * ROUTE, which is what ClearCrown's note argues for: gating wider than the
   * server draws a live button that 403s on every press, and gating narrower
   * hides a control somebody is entitled to. The PRIVILEGED half is gated
   * separately and by the server, via `canRepairData` in the policy, so a rep
   * sees the operational fields live and the structural ones disabled with the
   * reason. */
  const mayAct = can(ACTIONS.CC_CHAMPION_UPDATE, record)
  const current = currentValue(record, field)
  const value = next === null ? current : next

  const problem = editProblem({ field, current, next: value, reason, gate })
  const blockedByDeclaration = problem?.declare && !declared
  const ready = mayAct && gate.editable && (!problem || (problem.declare && declared))

  const reset = () => {
    setOpen(false); setNext(null); setReason(''); setStage('edit')
    setResult(null); setDeclared(false)
  }

  const run = async () => {
    setStage('working'); setResult(null)
    const r = await runFieldEdit({
      record, field, value, reason, gate, lockProtected: gate.lockProtected,
    })
    setResult(r)
    setStage('done')
    /* ⚠️ ONLY A CONFIRMED WRITE THAT THE RE-READ AGREES WITH REPAINTS THE BOARD.
     * A `mismatch` means the route said yes and HubSpot disagrees — patching on
     * that would put a value on the row that the record does not hold, which is
     * the two-sources-of-truth fault this board has been bitten by before. */
    if (r.verdict.kind === 'written' && r.confirmation?.state === 'confirmed') {
      const patch = editPatch(field, value)
      if (patch && onEdited) onEdited(String(record.id), patch)
    }
  }

  /* ---- NOT EDITABLE, AND ALWAYS WITH THE REASON ------------------------
   * Shown, never hidden. A rep who cannot see WHY a control is unavailable
   * concludes the feature is broken and asks for it to be fixed — the same
   * reading as a committee member with no email in OutreachPicker. */
  if (!gate.editable || !mayAct) {
    return (
      <span className={styles.fcLocked} title={gate.why || why(ACTIONS.CC_CHAMPION_UPDATE, record) || ''}>
        {gate.tier === 'privileged' ? 'privileged' : 'read-only'}
      </span>
    )
  }

  if (!open) {
    return (
      <button type="button" className={styles.fcOpen} onClick={() => setOpen(true)}>
        Correct
      </button>
    )
  }

  return (
    <div className={styles.fcPanel}>
      <div className={styles.fcHead}>
        Correct <b>{spec.label}</b>
        {gate.tier === 'privileged' && (
          <span className={styles.fcTier} title={policy.privilegedRequires}>privileged</span>
        )}
      </div>

      {spec.help && <p className={styles.fcHelp}>{spec.help}</p>}

      {/* ⚠️ BEFORE → AFTER, ALWAYS BOTH. The operator is looking at the value
          they are about to replace and the value they are replacing it with, in
          the same glance. This is the whole defence against a blind edit on a
          privileged field.

          ⚠️ AND THE LABEL NAMES THE PROPERTY, because this is the STORED value
          and the row above it may legitimately show something else. The
          normalizer transforms four of these on the way to the screen —
          `sr_state` "Georgia" renders as GA, `sr_date_status` is invented when
          absent, `sr_conference` is cleaned, and the date row shows the derived
          effective date. Labelling this "Now" alone would leave the operator
          unable to tell which of two different-looking values they were about
          to replace. */}
      <div className={styles.fcNow}>
        <span className={styles.fcNowK}>Now · {field}</span>
        <span className={styles.fcNowV}>{renderValue(field, current)}</span>
        {isDerivedField(field) && (
          <span className={styles.fcNowNote}>
            This is the stored value. The board shows a value derived from it, so the row above may
            read differently — and correcting this may not change what the board displays.
          </span>
        )}
      </div>

      {spec.kind === 'boolean' ? (
        <div className={styles.fcBoolRow}>
          <button
            type="button"
            className={value ? styles.fcBoolOn : styles.fcBoolOff}
            aria-pressed={Boolean(value)}
            onClick={() => { setNext(!value); setStage('edit'); setResult(null) }}
          >
            {value ? 'Locked' : 'Not locked'}
          </button>
          <span className={styles.fcBoolHint}>
            {value
              ? 'Automation cannot change the champion or its provenance on this record.'
              : 'Automation may set or change the champion on this record.'}
          </span>
        </div>
      ) : (
        <label className={styles.fcField}>
          <span className={styles.fcFieldK}>Change it to</span>
          <input
            className={styles.fcInput}
            type={spec.kind === 'date' ? 'date' : 'text'}
            value={value}
            placeholder={spec.kind === 'date' ? 'YYYY-MM-DD' : ''}
            onChange={(e) => { setNext(e.target.value); setStage('edit'); setResult(null) }}
          />
        </label>
      )}

      {value !== current && (
        <div className={styles.fcDiff}>
          {renderValue(field, current)} <span className={styles.fcArrow}>→</span>{' '}
          <b>{renderValue(field, value)}</b>
        </div>
      )}

      {/* ⚠️ THE REASON IS NOT OPTIONAL AND IT IS NOT DECORATION. It is written
          to the ledger and it is the only thing that will explain this change to
          whoever reads the record next. The floor exists because a one-word
          reason satisfies a required check and explains nothing. */}
      <label className={styles.fcField}>
        <span className={styles.fcFieldK}>
          Why — this goes to the ledger <span className={styles.fcReq}>required</span>
        </span>
        <textarea
          className={styles.fcReason}
          rows={3}
          value={reason}
          placeholder="What was wrong, and how the new value was established."
          onChange={(e) => { setReason(e.target.value); setStage('edit'); setResult(null) }}
        />
      </label>

      {problem?.declare && (
        <label className={styles.fcDeclare}>
          <input
            type="checkbox"
            checked={declared}
            onChange={(e) => setDeclared(e.target.checked)}
          />
          <span>Yes — clear this field deliberately. {problem.message}</span>
        </label>
      )}

      {problem && !problem.declare && stage === 'edit' && (
        <div className={styles.fcProblem}>{problem.message}</div>
      )}
      {blockedByDeclaration && stage === 'edit' && (
        <div className={styles.fcProblem}>Tick the box above to clear this field.</div>
      )}

      {stage === 'edit' && (
        <div className={styles.fcActions}>
          <button
            type="button"
            className={styles.fcGo}
            disabled={!ready}
            onClick={() => setStage('confirm')}
          >
            Review this correction
          </button>
          <button type="button" className={styles.fcCancel} onClick={reset}>Cancel</button>
        </div>
      )}

      {/* ⚠️ A SECOND, SPECIFIC ACT. Propose-don't-overwrite is the standing
          rule; this is a human deliberately replacing a recorded value on a
          live record, so it takes a deliberate gesture made against the actual
          before and after — not a tap on a button beside a text box. */}
      {stage === 'confirm' && (
        <div className={styles.fcConfirm}>
          <p className={styles.fcAsk}>
            Set <b>{spec.label}</b> to <b>{renderValue(field, value)}</b>?
          </p>
          <p className={styles.fcDetail}>
            It currently reads {renderValue(field, current)}. The old value and your reason both go
            to the ledger, and the record will be read back to confirm what it actually says
            afterwards.
          </p>
          <div className={styles.fcActions}>
            <button type="button" className={styles.fcGo} onClick={run}>
              Yes — write it
            </button>
            <button type="button" className={styles.fcCancel} onClick={() => setStage('edit')}>
              Back
            </button>
          </div>
        </div>
      )}

      {stage === 'working' && (
        <div className={styles.fcWorking}>Writing, then reading the record back…</div>
      )}

      {stage === 'done' && result && (
        <Verdict result={result} field={field} onClose={reset} />
      )}
    </div>
  )
}

/* ---- WHAT THE SERVER ACTUALLY SAID -------------------------------------
 *
 * ⚠️ ONLY `written` + `confirmed` IS A SUCCESS, AND IT TAKES BOTH.
 *
 * `written` alone means the route named the field — which it has done while
 * changing nothing, and which it will do for an enum value HubSpot then
 * rejects. So the re-read qualifies it, and the three qualified outcomes are
 * drawn as three different things rather than collapsed:
 *
 *   written + confirmed   → done, and the record says so
 *   written + mismatch    → ⚠️ NOT corrected, and it is the loudest state here
 *   written + unverified  → the write landed, what it reads now is unknown
 */
function Verdict({ result, field, onClose }) {
  const { verdict, confirmation } = result

  if (verdict.kind === 'written') {
    if (confirmation?.state === 'confirmed') {
      return (
        <div className={styles.fcOk}>
          <b>Corrected.</b> {confirmation.message} {verdict.message}
          <button type="button" className={styles.fcCancel} onClick={onClose}>Close</button>
        </div>
      )
    }
    if (confirmation?.state === 'mismatch') {
      return (
        <div className={styles.fcErr}>
          <b>The write was accepted and the record does not agree.</b>
          <div>{confirmation.message}</div>
          <div className={styles.fcHint}>{confirmation.hint}</div>
          <button type="button" className={styles.fcCancel} onClick={onClose}>Close</button>
        </div>
      )
    }
    return (
      <div className={styles.fcWarn}>
        <b>Written, not verified.</b>
        <div>{confirmation?.message || verdict.message}</div>
        {confirmation?.hint && <div className={styles.fcHint}>{confirmation.hint}</div>}
        <button type="button" className={styles.fcCancel} onClick={onClose}>Close</button>
      </div>
    )
  }

  /* `denied` is the grant gate and it gets its own wording, because the fix is
   * a named, specific thing — an exec sets `cc_data_repair` — and "refused" on
   * its own would send somebody to ask for the wrong thing. */
  return (
    <div className={verdict.kind === 'denied' ? styles.fcDenied : styles.fcRefused}>
      <b>
        {verdict.kind === 'denied' ? 'Not permitted — nothing was written.'
          : verdict.kind === 'nothing-written' ? 'Nothing was written.'
            : verdict.kind === 'error' ? 'Did not run.'
              : 'Refused.'}
      </b>
      <div>{verdict.message}</div>
      {verdict.hint && <div className={styles.fcHint}>{verdict.hint}</div>}
      <button type="button" className={styles.fcCancel} onClick={onClose}>Close</button>
    </div>
  )
}

/* ---- THE ROW WRAPPER ---------------------------------------------------
 * A record-grid row that carries its own correction control. The editor expands
 * BENEATH the row rather than replacing it, so the value being corrected stays
 * visible while it is being corrected.
 */
export function EditableRow({ k, field, record, policy, onEdited, children }) {
  return (
    <div className={styles.fcRow}>
      <div className={styles.fcRowTop}>
        <div className={styles.fcRowK}>{k}</div>
        <div className={styles.fcRowV}>
          {children}
          <FieldCorrect
            /* Keyed by record AND field — the drawer is reused when the operator
               opens a different championship, and without this an open form
               would carry the previous record's typed value and reason onto a
               different record. */
            key={`${record?.id}:${field}`}
            record={record}
            field={field}
            policy={policy}
            onEdited={onEdited}
          />
        </div>
      </div>
    </div>
  )
}
